import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export interface ServiceToken {
  id: string;
  name: string;
  team_id: string;
  team_name?: string;
  project_ids: string[];
  projects: Array<{ id: string; name: string; slug: string }>;
  scopes: TokenScope[];
  expires_at?: string;
  last_used_at?: string;
  created_at: string;
}

export interface TokenWithValue extends ServiceToken {
  token: string;
}

export type TokenScope = 'read' | 'write';

export const TOKEN_SCOPES: { value: TokenScope; label: string; description: string }[] = [
  {
    value: 'read',
    label: 'Read',
    description: 'Read and export secret values',
  },
  {
    value: 'write',
    label: 'Write',
    description: 'Create, update, and delete secrets',
  },
];

interface TokensResponse {
  tokens: ServiceToken[];
}

interface CreateTokenRequest {
  project_ids: string[];
  name: string;
  scopes: TokenScope[];
  expires_in_days?: number;
}

interface CreateTokenResponse {
  id: string;
  token: string;
  name: string;
  team_id: string;
  project_ids: string[];
  projects: Array<{ id: string; name: string; slug: string }>;
  scopes: TokenScope[];
  expires_at?: string;
  created_at: string;
}

export function useTokens() {
  const queryClient = useQueryClient();

  const tokensQuery = useQuery({
    queryKey: ['tokens'],
    queryFn: async () => {
      const response = await apiClient.get<TokensResponse>('/api/v1/tokens');
      return response.data.tokens;
    },
    staleTime: 1000 * 60,
  });

  const createTokenMutation = useMutation({
    mutationFn: async (data: CreateTokenRequest): Promise<TokenWithValue> => {
      const response = await apiClient.post<CreateTokenResponse>('/api/v1/tokens', data);
      return {
        id: response.data.id,
        token: response.data.token,
        name: response.data.name,
        team_id: response.data.team_id,
        project_ids: response.data.project_ids,
        projects: response.data.projects,
        scopes: response.data.scopes,
        expires_at: response.data.expires_at,
        created_at: response.data.created_at,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
    },
  });

  const revokeTokenMutation = useMutation({
    mutationFn: async (tokenId: string) => {
      await apiClient.delete(`/api/v1/tokens/${tokenId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tokens'] });
    },
  });

  return {
    tokens: tokensQuery.data ?? [],
    isLoading: tokensQuery.isLoading,
    error: tokensQuery.error,
    refetch: tokensQuery.refetch,

    createToken: createTokenMutation.mutateAsync,
    isCreating: createTokenMutation.isPending,
    createError: createTokenMutation.error,
    createdToken: createTokenMutation.data,
    resetCreateToken: createTokenMutation.reset,

    revokeToken: revokeTokenMutation.mutateAsync,
    isRevoking: revokeTokenMutation.isPending,
    revokeError: revokeTokenMutation.error,
  };
}

export function formatTokenExpiry(expiresAt?: string): string {
  if (!expiresAt) return 'Never expires';

  const expiry = new Date(expiresAt);
  const now = new Date();

  if (expiry < now) {
    return 'Expired';
  }

  const diffMs = expiry.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 1) return 'Expires tomorrow';
  if (diffDays <= 7) return `Expires in ${diffDays} days`;
  if (diffDays <= 30) return `Expires in ${Math.ceil(diffDays / 7)} weeks`;

  return expiry.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatLastUsed(lastUsedAt?: string): string {
  if (!lastUsedAt) return 'Never used';

  const lastUsed = new Date(lastUsedAt);
  const now = new Date();
  const diffMs = now.getTime() - lastUsed.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return lastUsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
